/**
 * API contracts for the `devices-printing` module (M9) — zod schemas + inferred types
 * shared by apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the devices-printing module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * Two halves:
 *  1. DEVICES — scanner fleet: heartbeat, staleness, LPN range leases.
 *  2. PRINTING — printer registry, the serialized print queue, ZPL label
 *     templates (server-side ONLY) and A4 PDF documents.
 *
 * The printing half is a full redesign per ARCHITECTURE §8.4; every schema here
 * exists to close one of the 17 defects catalogued in
 * `docs/recon/printing-scanners.md`. The two that shape the wire format most:
 *
 *  - **No client-supplied host/port anywhere.** A print request names a REGISTERED
 *    `printerId` (or a station + kind, resolved server-side). The old system let
 *    any authenticated caller open a raw socket to any host in the cluster
 *    (recon A6 #8) — the shape of these schemas makes that unrepresentable.
 *  - **No client-supplied ZPL, ever.** Templates live on the server; the client
 *    sends a typed payload. The old system built ZPL by string concatenation in
 *    eight browser files with four divergent, lossy escapers (recon A6 #6).
 */
import { z } from 'zod';

import { uuidSchema } from '../schemas/common.js';

// ===========================================================================
// DEVICES
// ===========================================================================

/**
 * Fleet status, derived server-side from `devices.last_seen_at` so every client
 * (web list, device detail, future dashboards) draws the same conclusion from
 * the same numbers. `inactive` wins over everything: a deactivated scanner is
 * not "offline", it is retired.
 */
export const deviceStatusSchema = z.enum(['online', 'idle', 'stale', 'offline', 'inactive']);
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

/**
 * Staleness thresholds, in seconds since the last heartbeat. The Android app
 * heartbeats every 60 s while online (ARCHITECTURE §6), so `online` tolerates a
 * couple of missed beats before the fleet list starts shouting.
 */
export const DEVICE_STALENESS = {
  /** heartbeat within 3 min ⇒ green */
  onlineSeconds: 180,
  /** within 15 min ⇒ amber "idle" (screen off, charging in the dock) */
  idleSeconds: 900,
  /** within 4 h ⇒ red "stale" — matches the app's own 4 h offline warning */
  staleSeconds: 4 * 3600,
} as const;

/** Alarm thresholds the fleet list renders as a warning chip. */
export const DEVICE_ALARMS = {
  lowBatteryPct: 20,
  criticalBatteryPct: 10,
  /** an outbox this deep means commands are not reaching the server */
  outboxDepthWarning: 50,
  /** reference data older than this is no longer safe to pick against */
  snapshotAgeWarningSeconds: 4 * 3600,
  /** LPN lease this close to exhaustion ⇒ prompt a top-up while still online */
  lpnRemainingWarning: 100,
} as const;

export const deviceSchema = z.object({
  id: uuidSchema,
  serial: z.string(),
  name: z.string(),
  model: z.string().nullable(),
  isActive: z.boolean(),

  lastSeenAt: z.iso.datetime().nullable(),
  batteryPct: z.number().int().nullable(),
  batteryHealth: z.string().nullable(),
  wifiSsid: z.string().nullable(),
  wifiRssi: z.number().int().nullable(),
  appVersion: z.string().nullable(),
  storageFreeMb: z.number().int().nullable(),
  outboxDepth: z.number().int().nullable(),
  snapshotAgeS: z.number().int().nullable(),

  lastUserId: uuidSchema.nullable(),
  lastUserName: z.string().nullable(),

  lpnRangeStart: z.number().int().nullable(),
  lpnRangeEnd: z.number().int().nullable(),
  lpnNext: z.number().int().nullable(),
  lpnLeasedAt: z.iso.datetime().nullable(),
  /** LPNs left in the current lease, or null when the device has never leased. */
  lpnRemaining: z.number().int().nullable(),

  /** Derived (never stored): see `deviceStatusFor`. */
  status: deviceStatusSchema,
  /** Derived alarm keys, e.g. `battery_low`, `outbox_deep`, `snapshot_stale`. */
  alarms: z.array(z.string()),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Device = z.infer<typeof deviceSchema>;

/**
 * `POST /api/devices/heartbeat` — and command `device.heartbeat`, so the same
 * payload works from the sync outbox when a scanner comes back online with a
 * backlog. Every field is optional: a phone with no battery API must still be
 * able to say "I am here".
 */
export const deviceHeartbeatSchema = z.object({
  /** Omitted by a device session (taken from the session); required from web/tests. */
  deviceId: uuidSchema.optional(),
  /**
   * The scanner's hardware serial. The Android client sends this (and no
   * `deviceId`) — see `HeartbeatRequest` in apps/android …/data/net/Dtos.kt.
   * The server resolves it against the `devices` row that
   * `DeviceRegistrationService.registerOnLogin` created; it never registers a
   * device, so an unknown serial is `E_DEVICE_NOT_REGISTERED`.
   */
  serial: z.string().trim().min(1).max(64).optional(),
  batteryPct: z.number().int().min(0).max(100).nullish(),
  batteryHealth: z.string().max(40).nullish(),
  wifiSsid: z.string().max(64).nullish(),
  wifiRssi: z.number().int().min(-120).max(0).nullish(),
  appVersion: z.string().max(40).nullish(),
  storageFreeMb: z.number().int().min(0).nullish(),
  /** Commands still queued locally — the staleness signal that matters most. */
  outboxDepth: z.number().int().min(0).nullish(),
  /** Age of the device's reference-data snapshot, in seconds. */
  snapshotAgeS: z.number().int().min(0).nullish(),
});
export type DeviceHeartbeat = z.infer<typeof deviceHeartbeatSchema>;

export const deviceHeartbeatResultSchema = z.object({
  deviceId: uuidSchema,
  status: deviceStatusSchema,
  alarms: z.array(z.string()),
  serverTime: z.iso.datetime(),
  /** Server-side staleness budget so the app can draw its own banner. */
  staleness: z.object({
    onlineSeconds: z.number().int(),
    idleSeconds: z.number().int(),
    staleSeconds: z.number().int(),
  }),
});
export type DeviceHeartbeatResult = z.infer<typeof deviceHeartbeatResultSchema>;

/** `PATCH /api/devices/:id` — admin rename / retire. */
export const deviceUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  model: z.string().trim().max(80).nullish(),
  isActive: z.boolean().optional(),
});
export type DeviceUpdate = z.infer<typeof deviceUpdateSchema>;

/**
 * LPN range leasing (ARCHITECTURE §6). A device leases a block of the `LP1…`
 * namespace while online; offline it mints labels from that block, so an
 * hours-long offline window still produces collision-free pallet IDs.
 */
export const LPN_LEASE_SIZE = 1000;
/** `LP1#######` — 7 digits after the namespace digit. */
export const LPN_DEVICE_MIN = 1;
export const LPN_DEVICE_MAX = 9_999_999;

export const lpnLeaseRequestSchema = z.object({
  deviceId: uuidSchema.optional(),
  /** How many LPNs to lease. Defaults to LPN_LEASE_SIZE. */
  size: z.number().int().min(1).max(5000).optional(),
  /**
   * Wire alias for `size`. The Android client posts `{count: 1000}` (see
   * `LpnLeaseRequest` in apps/android …/data/net/Dtos.kt); `size` wins when a
   * caller sends both.
   */
  count: z.number().int().min(1).max(5000).optional(),
  /**
   * Force a fresh block even when the current lease still has room. The app
   * sets this only when the operator explicitly asks for a top-up.
   */
  force: z.boolean().default(false),
});
export type LpnLeaseRequest = z.infer<typeof lpnLeaseRequestSchema>;

export const lpnLeaseSchema = z.object({
  deviceId: uuidSchema,
  rangeStart: z.number().int(),
  rangeEnd: z.number().int(),
  next: z.number().int(),
  remaining: z.number().int(),
  leasedAt: z.iso.datetime(),
  /** Ready-to-print sample of the first code in the lease, e.g. `LP1000001`. */
  firstLpn: z.string(),
  lastLpn: z.string(),
  /** True when this call minted a new block (false ⇒ existing lease returned). */
  issued: z.boolean(),
});
export type LpnLease = z.infer<typeof lpnLeaseSchema>;

/** One row of the device history tab (audit + heartbeat events). */
export const deviceHistoryEntrySchema = z.object({
  id: z.string(),
  at: z.iso.datetime(),
  action: z.string(),
  userId: uuidSchema.nullable(),
  userName: z.string().nullable(),
  changes: z.unknown().nullable(),
  context: z.unknown().nullable(),
});
export type DeviceHistoryEntry = z.infer<typeof deviceHistoryEntrySchema>;

// ===========================================================================
// PRINTERS
// ===========================================================================

export const printerKindSchema = z.enum(['label', 'document']);
export type PrinterKind = z.infer<typeof printerKindSchema>;

/**
 * A hostname or IPv4/IPv6 literal. Deliberately NOT a URL: the print path is a
 * raw TCP socket, and accepting a URL invites someone to point it at `file://`
 * or an internal HTTP service. Registry entries are admin-only; print requests
 * never carry a host at all.
 */
export const printerHostSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .regex(
    /^(?:\[[0-9a-fA-F:]+\]|[0-9a-fA-F:]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)$/,
    'invalid printer host',
  );

/** Zebra label stock we keep loaded. Drives the default template geometry. */
export const printerStockCodeSchema = z.enum(['105x148', '60x30', '101x152', 'a4']);
export type PrinterStockCode = z.infer<typeof printerStockCodeSchema>;

export const printerStatusSchema = z.object({
  /** Answered the poll at all. */
  online: z.boolean(),
  ready: z.boolean().nullable(),
  paused: z.boolean().nullable(),
  mediaOut: z.boolean().nullable(),
  ribbonOut: z.boolean().nullable(),
  headOpen: z.boolean().nullable(),
  /** Jobs the printer itself still holds in its buffer, when it reports them. */
  jobsQueued: z.number().int().nullable(),
  /** Raw SGD/HQES text, kept for support tickets. */
  raw: z.string().nullable(),
  /** `sgd` (JSON on :9200) or `hqes` (ASCII fallback on :9100). */
  source: z.enum(['sgd', 'hqes', 'unreachable']),
  at: z.iso.datetime(),
  error: z.string().nullable(),
});
export type PrinterStatus = z.infer<typeof printerStatusSchema>;

export const printerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  host: z.string(),
  port: z.number().int(),
  model: z.string().nullable(),
  kind: printerKindSchema,
  station: z.string().nullable(),
  stockCode: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  lastStatus: printerStatusSchema.nullable(),
  lastSeenAt: z.iso.datetime().nullable(),
  /** Live queue depth (`queued` + `sending`), joined in by the list endpoint. */
  queuedJobs: z.number().int(),
  failedJobs: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Printer = z.infer<typeof printerSchema>;

export const printerCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  host: printerHostSchema,
  port: z.number().int().min(1).max(65535).default(9100),
  model: z.string().trim().max(80).nullish(),
  kind: printerKindSchema.default('label'),
  station: z.string().trim().max(80).nullish(),
  stockCode: z.string().trim().max(40).nullish(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type PrinterCreate = z.infer<typeof printerCreateSchema>;

export const printerUpdateSchema = printerCreateSchema.partial();
export type PrinterUpdate = z.infer<typeof printerUpdateSchema>;

/** `POST /api/printers/:id/test-print` */
export const printerTestPrintSchema = z.object({
  /** Freetext line on the test label. Swedish — labels are Swedish-only. */
  note: z.string().trim().max(60).optional(),
});
export type PrinterTestPrint = z.infer<typeof printerTestPrintSchema>;

// ===========================================================================
// LABEL TEMPLATES (server-side only)
// ===========================================================================

export const labelKindSchema = z.enum(['pallet_label', 'bin_label', 'article_label', 'test_label']);
export type LabelKind = z.infer<typeof labelKindSchema>;

export const documentKindSchema = z.enum(['pick_list', 'packing_slip']);
export type DocumentKind = z.infer<typeof documentKindSchema>;

export const printJobKindSchema = z.union([labelKindSchema, documentKindSchema]);
export type PrintJobKind = z.infer<typeof printJobKindSchema>;

/**
 * Physical geometry per label kind (MODULES.md M9). ONE table — the old system
 * derived label geometry from four different, partly fictional DPI constants in
 * five files (recon A6 #17).
 */
export const LABEL_SPECS = {
  pallet_label: { widthMm: 105, heightMm: 148, defaultDpi: 203 },
  bin_label: { widthMm: 60, heightMm: 30, defaultDpi: 203 },
  article_label: { widthMm: 60, heightMm: 30, defaultDpi: 203 },
  test_label: { widthMm: 60, heightMm: 30, defaultDpi: 203 },
} as const satisfies Record<LabelKind, { widthMm: number; heightMm: number; defaultDpi: number }>;

/** Printer resolutions we support. 8 dots/mm = 203 dpi, 12 = 300, 24 = 600. */
export const printerDpiSchema = z.union([z.literal(203), z.literal(300), z.literal(600)]);
export type PrinterDpi = z.infer<typeof printerDpiSchema>;

/**
 * Barcode payload limits. Exceeding one is a REFUSAL (`E_VALIDATION` with
 * `field`/`max`/`length` params), never a silent truncation: the old system cut
 * long codes at 100/120 chars and still printed a scannable — but wrong —
 * barcode (recon A6 #6).
 */
export const BARCODE_LIMITS = {
  /** Code 128 subset B/C on a 105 mm label at 203 dpi. */
  code128MaxLength: 48,
  /** DataMatrix ECC200, the 2D code on our pallet labels. */
  dataMatrixMaxLength: 200,
} as const;

const labelTextSchema = z.string().trim().max(120);

export const palletLabelPayloadSchema = z.object({
  /** `LP0#######` / `LP1#######`. Encoded verbatim — never escaped, never cut. */
  lpn: z.string().trim().min(1).max(BARCODE_LIMITS.code128MaxLength),
  articleNo: labelTextSchema.optional(),
  articleName: labelTextSchema.optional(),
  qty: z.string().trim().max(24).optional(),
  unit: z.string().trim().max(12).optional(),
  lotNo: labelTextSchema.optional(),
  bestBefore: z.string().trim().max(20).optional(),
  binCode: labelTextSchema.optional(),
  supplierName: labelTextSchema.optional(),
  receivedAt: z.string().trim().max(20).optional(),
  orderNo: labelTextSchema.optional(),
  /** Supplier SSCC captured as an alias — printed as text, not as a barcode. */
  ssccAlias: labelTextSchema.optional(),
});
export type PalletLabelPayload = z.infer<typeof palletLabelPayloadSchema>;

export const binLabelPayloadSchema = z.object({
  binCode: z.string().trim().min(1).max(BARCODE_LIMITS.code128MaxLength),
  areaName: labelTextSchema.optional(),
  shelfCode: labelTextSchema.optional(),
  isPickLocation: z.boolean().optional(),
});
export type BinLabelPayload = z.infer<typeof binLabelPayloadSchema>;

export const articleLabelPayloadSchema = z.object({
  articleNo: z.string().trim().min(1).max(BARCODE_LIMITS.code128MaxLength),
  articleName: labelTextSchema,
  barcode: z.string().trim().max(BARCODE_LIMITS.code128MaxLength).optional(),
  unit: z.string().trim().max(12).optional(),
  categoryName: labelTextSchema.optional(),
  lotNo: labelTextSchema.optional(),
  bestBefore: z.string().trim().max(20).optional(),
});
export type ArticleLabelPayload = z.infer<typeof articleLabelPayloadSchema>;

export const testLabelPayloadSchema = z.object({
  printerName: labelTextSchema,
  note: labelTextSchema.optional(),
  at: z.string().trim().max(30).optional(),
});
export type TestLabelPayload = z.infer<typeof testLabelPayloadSchema>;

/** Discriminated union so `kind` picks the payload shape on both ends. */
export const LABEL_VARIANTS = [
  z.object({ kind: z.literal('pallet_label'), payload: palletLabelPayloadSchema }),
  z.object({ kind: z.literal('bin_label'), payload: binLabelPayloadSchema }),
  z.object({ kind: z.literal('article_label'), payload: articleLabelPayloadSchema }),
  z.object({ kind: z.literal('test_label'), payload: testLabelPayloadSchema }),
] as const;

export const labelRequestSchema = z.discriminatedUnion('kind', [...LABEL_VARIANTS]);
export type LabelRequest = z.infer<typeof labelRequestSchema>;

// ---------------------------------------------------------------------------
// A4 documents
// ---------------------------------------------------------------------------

export const pickListLinePayloadSchema = z.object({
  sort: z.number().int().optional(),
  binCode: z.string().max(40).optional(),
  articleNo: z.string().max(60),
  articleName: z.string().max(160),
  qty: z.string().max(24),
  unit: z.string().max(12).optional(),
  lotNo: z.string().max(60).optional(),
  bestBefore: z.string().max(20).optional(),
  lpn: z.string().max(40).optional(),
  note: z.string().max(120).optional(),
});
export type PickListLinePayload = z.infer<typeof pickListLinePayloadSchema>;

export const pickListPayloadSchema = z.object({
  roundNo: z.string().max(40),
  deliveryDate: z.string().max(20).optional(),
  restaurantName: z.string().max(120).optional(),
  orderNo: z.string().max(40).optional(),
  flowName: z.string().max(80).optional(),
  printedAt: z.string().max(30).optional(),
  lines: z.array(pickListLinePayloadSchema).min(1).max(500),
});
export type PickListPayload = z.infer<typeof pickListPayloadSchema>;

export const packingSlipLinePayloadSchema = z.object({
  articleNo: z.string().max(60),
  articleName: z.string().max(160),
  qty: z.string().max(24),
  unit: z.string().max(12).optional(),
  lotNo: z.string().max(60).optional(),
  bestBefore: z.string().max(20).optional(),
});
export type PackingSlipLinePayload = z.infer<typeof packingSlipLinePayloadSchema>;

export const packingSlipPayloadSchema = z.object({
  shipmentNo: z.string().max(40),
  orderNo: z.string().max(40).optional(),
  restaurantName: z.string().max(120),
  deliveryAddress: z.string().max(240).optional(),
  deliveryDate: z.string().max(20).optional(),
  palletCount: z.number().int().min(0).optional(),
  /** Absolute deep link encoded as a QR code (`/portal/receive/<token>`). */
  qrUrl: z.string().max(400).optional(),
  printedAt: z.string().max(30).optional(),
  lines: z.array(packingSlipLinePayloadSchema).min(1).max(500),
});
export type PackingSlipPayload = z.infer<typeof packingSlipPayloadSchema>;

export const DOCUMENT_VARIANTS = [
  z.object({ kind: z.literal('pick_list'), payload: pickListPayloadSchema }),
  z.object({ kind: z.literal('packing_slip'), payload: packingSlipPayloadSchema }),
] as const;

export const documentRequestSchema = z.discriminatedUnion('kind', [...DOCUMENT_VARIANTS]);
export type DocumentRequest = z.infer<typeof documentRequestSchema>;

// ===========================================================================
// PRINT JOBS
// ===========================================================================

export const printJobStatusSchema = z.enum(['queued', 'sending', 'sent', 'failed', 'dead']);
export type PrintJobStatus = z.infer<typeof printJobStatusSchema>;

/**
 * Printer targeting. Exactly one of `printerId` / `station` is meaningful; the
 * server resolves a station (plus `kind`) to a registered printer. There is no
 * third option and there is no `host`.
 */
export const printTargetSchema = z.object({
  printerId: uuidSchema.optional(),
  station: z.string().trim().max(80).optional(),
});
export type PrintTarget = z.infer<typeof printTargetSchema>;

/**
 * `POST /api/print/label` and command `print.label` (scanner-facing: the
 * Android palletize flow prints labels through the sync outbox).
 */
const printLabelExtras = {
  printerId: uuidSchema.optional(),
  station: z.string().trim().max(80).optional(),
  copies: z.number().int().min(1).max(50).default(1),
  /** Override the printer's resolution when the label stock demands it. */
  dpi: printerDpiSchema.optional(),
};

export const printLabelRequestSchema = z.discriminatedUnion('kind', [
  LABEL_VARIANTS[0].extend(printLabelExtras),
  LABEL_VARIANTS[1].extend(printLabelExtras),
  LABEL_VARIANTS[2].extend(printLabelExtras),
  LABEL_VARIANTS[3].extend(printLabelExtras),
]);
export type PrintLabelRequest = z.infer<typeof printLabelRequestSchema>;

/** `POST /api/print/document` and command `print.document`. */
const printDocumentExtras = {
  printerId: uuidSchema.optional(),
  station: z.string().trim().max(80).optional(),
  copies: z.number().int().min(1).max(20).default(1),
  /**
   * `false` renders + persists the PDF without queueing it to a printer — the
   * "download" path. Documents are ALWAYS persisted (recon A6: the old system
   * kept shipment labels in process memory only).
   */
  print: z.boolean().default(true),
};

export const printDocumentRequestSchema = z.discriminatedUnion('kind', [
  DOCUMENT_VARIANTS[0].extend(printDocumentExtras),
  DOCUMENT_VARIANTS[1].extend(printDocumentExtras),
]);
export type PrintDocumentRequest = z.infer<typeof printDocumentRequestSchema>;

export const printJobSchema = z.object({
  id: uuidSchema,
  printerId: uuidSchema,
  printerName: z.string().nullable(),
  kind: z.string(),
  copies: z.number().int(),
  status: printJobStatusSchema,
  attempts: z.number().int(),
  error: z.string().nullable(),
  createdBy: uuidSchema.nullable(),
  createdByName: z.string().nullable(),
  pdfFileId: uuidSchema.nullable(),
  /** Present only on the detail endpoint — job lists never carry the payload. */
  zpl: z.string().nullable().optional(),
  sentAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PrintJob = z.infer<typeof printJobSchema>;

export const printJobResultSchema = z.object({
  jobId: uuidSchema,
  printerId: uuidSchema,
  status: printJobStatusSchema,
  /** File id of the rendered PDF for document jobs. */
  fileId: uuidSchema.nullable(),
});
export type PrintJobResult = z.infer<typeof printJobResultSchema>;

/** `POST /api/print/jobs/:id/reprint` — the history-driven reprint (M9 spec). */
export const printReprintSchema = z.object({
  jobId: uuidSchema,
  /** Send to a different printer than the original (dead printer, other station). */
  printerId: uuidSchema.optional(),
  copies: z.number().int().min(1).max(50).optional(),
});
export type PrintReprint = z.infer<typeof printReprintSchema>;

/** Retry limits for the per-printer queue. Exceeding `maxAttempts` ⇒ `dead`. */
export const PRINT_RETRY = {
  maxAttempts: 4,
  baseDelayMs: 2_000,
  maxDelayMs: 60_000,
} as const;

/** Socket budgets for the raw ZPL path (recon A6 #3: one path had NO timeout). */
export const PRINT_TIMEOUTS = {
  connectMs: 5_000,
  writeMs: 10_000,
  statusMs: 4_000,
} as const;

// ---------------------------------------------------------------------------
// preview
// ---------------------------------------------------------------------------

export const templatePreviewFormatSchema = z.enum(['png', 'zpl']);
export type TemplatePreviewFormat = z.infer<typeof templatePreviewFormatSchema>;

/**
 * `POST /api/print/templates/:kind/preview` — renders the SAME server-side
 * template the queue would send, so what an operator previews is what the
 * printer receives. `zpl` returns the source; `png` runs it through the
 * self-hosted rasterizer.
 */
const templatePreviewExtras = {
  format: templatePreviewFormatSchema.default('png'),
  dpi: printerDpiSchema.optional(),
  /** Raster upscale for on-screen legibility (PNG only). */
  scale: z.number().int().min(1).max(4).default(2),
};

export const templatePreviewRequestSchema = z.discriminatedUnion('kind', [
  LABEL_VARIANTS[0].extend(templatePreviewExtras),
  LABEL_VARIANTS[1].extend(templatePreviewExtras),
  LABEL_VARIANTS[2].extend(templatePreviewExtras),
  LABEL_VARIANTS[3].extend(templatePreviewExtras),
]);
export type TemplatePreviewRequest = z.infer<typeof templatePreviewRequestSchema>;

export const templateInfoSchema = z.object({
  kind: labelKindSchema,
  widthMm: z.number(),
  heightMm: z.number(),
  defaultDpi: z.number().int(),
  /** Swedish description — labels and their documentation stay Swedish. */
  description: z.string(),
});
export type TemplateInfo = z.infer<typeof templateInfoSchema>;

// ===========================================================================
// derived helpers (shared by API and web so both agree)
// ===========================================================================

export interface DeviceStatusInput {
  isActive: boolean;
  lastSeenAt: Date | string | null;
  now?: Date | number;
}

/** Seconds since a device last checked in, or `null` when it never has. */
export function deviceAgeSeconds(
  lastSeenAt: Date | string | null,
  now: Date | number = Date.now(),
): number | null {
  if (!lastSeenAt) return null;
  const seen = lastSeenAt instanceof Date ? lastSeenAt.getTime() : Date.parse(lastSeenAt);
  if (Number.isNaN(seen)) return null;
  const nowMs = now instanceof Date ? now.getTime() : now;
  return Math.max(0, Math.round((nowMs - seen) / 1000));
}

/** The ONE staleness rule. API and web both call this — no second copy. */
export function deviceStatusFor(input: DeviceStatusInput): DeviceStatus {
  if (!input.isActive) return 'inactive';
  const age = deviceAgeSeconds(input.lastSeenAt, input.now ?? Date.now());
  if (age === null) return 'offline';
  if (age <= DEVICE_STALENESS.onlineSeconds) return 'online';
  if (age <= DEVICE_STALENESS.idleSeconds) return 'idle';
  if (age <= DEVICE_STALENESS.staleSeconds) return 'stale';
  return 'offline';
}

export interface DeviceAlarmInput {
  batteryPct?: number | null;
  outboxDepth?: number | null;
  snapshotAgeS?: number | null;
  lpnRemaining?: number | null;
  status: DeviceStatus;
}

/**
 * Alarm keys for the fleet list. Returned by the API (so a dashboard and the
 * list never disagree) and rendered through `devicesPrinting:alarms.<key>`.
 */
export function deviceAlarmsFor(input: DeviceAlarmInput): string[] {
  const alarms: string[] = [];
  if (input.status === 'stale' || input.status === 'offline') alarms.push('not_seen');
  if (typeof input.batteryPct === 'number') {
    if (input.batteryPct <= DEVICE_ALARMS.criticalBatteryPct) alarms.push('battery_critical');
    else if (input.batteryPct <= DEVICE_ALARMS.lowBatteryPct) alarms.push('battery_low');
  }
  if ((input.outboxDepth ?? 0) >= DEVICE_ALARMS.outboxDepthWarning) alarms.push('outbox_deep');
  if ((input.snapshotAgeS ?? 0) >= DEVICE_ALARMS.snapshotAgeWarningSeconds) {
    alarms.push('snapshot_stale');
  }
  if (
    typeof input.lpnRemaining === 'number' &&
    input.lpnRemaining <= DEVICE_ALARMS.lpnRemainingWarning
  ) {
    alarms.push('lpn_low');
  }
  return alarms;
}

/** `LP1` + 7 digits — the DEVICE half of the LPN namespace (§6, core/stock). */
export function formatDeviceLpn(sequence: number): string {
  if (!Number.isInteger(sequence) || sequence < LPN_DEVICE_MIN || sequence > LPN_DEVICE_MAX) {
    throw new RangeError(`device LPN out of range: ${sequence}`);
  }
  return `LP1${String(sequence).padStart(7, '0')}`;
}

/** Dots for a millimetre measurement at a given resolution. ONE conversion. */
export function mmToDots(mm: number, dpi: number): number {
  return Math.round((mm * dpi) / 25.4);
}

/** Exponential backoff with a ceiling, used by the per-printer queue. */
export function printRetryDelayMs(attempt: number): number {
  const raw = PRINT_RETRY.baseDelayMs * 2 ** Math.max(0, attempt - 1);
  return Math.min(raw, PRINT_RETRY.maxDelayMs);
}

/** Command types this module registers with the CommandBus. */
export const DEVICES_PRINTING_COMMANDS = {
  deviceHeartbeat: 'device.heartbeat',
  deviceLpnLease: 'device.lpnLease',
  deviceUpdate: 'device.update',
  printLabel: 'print.label',
  printDocument: 'print.document',
  printReprint: 'print.reprint',
  printCancel: 'print.cancel',
  printerCreate: 'printer.create',
  printerUpdate: 'printer.update',
  printerDelete: 'printer.delete',
  printerTest: 'printer.test',
} as const;
export type DevicesPrintingCommand =
  (typeof DEVICES_PRINTING_COMMANDS)[keyof typeof DEVICES_PRINTING_COMMANDS];
